import { ShareTarget } from '../../services/sharing/ShareTarget'
/**
 * Right column — the canvas (file / doc view) and its own side panel.
 *
 * The side column is the canvas's own (RFC 0273): scoped to the OPEN DOCUMENT
 * rather than the project, so it is always about the thing being looked at.
 *
 * The header is real (RFC 0266 §3f): breadcrumbs whose depth adapts to which
 * panels are visible, per-crumb truncation with the full text in a title, and a
 * close that routes through the shell's single closeFile() so the button, Esc,
 * and any future palette command share one path. The renderer itself is a later
 * slice — a viewer needs documents (RFC 0263) or a checkout (RFC 0264) to view.
 *
 * The column mounts ONLY when a file is open (the shell enforces this): with
 * nothing open, the session surface takes the full width rather than sitting
 * beside an empty frame.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import { type AppSummary, projectSources } from '@mod/core/model'
import { Columns2, MessageCircle, PanelLeft, PanelRight, X } from 'lucide-react'
import {
  type Ref,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { BranchView } from '../../features/branches/BranchView'
import { branchPaneOf, isBranchId } from '../../features/branches/branchPane'
import {
  CodeFilesColumn,
  useFilesPrefs,
} from '../../features/code/CodeFilesColumn'
import { CodeFileView } from '../../features/code/CodeFileView'
import { DocumentComments } from '../../features/documents/DocumentComments'
import { DocumentCommentsButton } from '../../features/documents/DocumentCommentsButton'
import type { LinkOpenTarget } from '../../features/documents/DocumentLink'
import { DocumentView } from '../../features/documents/DocumentView'
import {
  type VersionPreview,
  VersionPreviewPane,
} from '../../features/documents/VersionPreview'
import {
  historyProjectOf,
  isHistoryId,
} from '../../features/projectHistory/historyPane'
import { ProjectHistoryView } from '../../features/projectHistory/ProjectHistoryView'
import { WebView } from '../../features/web/WebView'
import { isWebId, webUrlOf } from '../../features/web/webPage'
import { useIconApp } from '../../services/apps/AppIcon'
import type { AppViewController } from '../../services/apps/appControl'
import {
  codeFileFromId,
  isCodeId,
  parseCodeId,
} from '../../services/code/codeFile'
import {
  hasPendingPassage,
  onPendingPassage,
  takePendingPassage,
} from '../../services/documents/pendingPassage'
import { useDocument } from '../../services/world/repo/useDocument'
import { DocumentTasks } from '../tasks/DocumentTasks'
import { AppTile } from './AppTile'
import { DocumentHolders } from './DocumentHolders'
import { MissingAppView } from './MissingAppView'
import { Panel, PanelAction } from './Panel'
import {
  resolveSection,
  SidePanelBody,
  SidePanelTabs,
  sideSections,
} from './SidePanelSections'
import { useSideWidth } from './sideWidth'
import { type Subject, withAppCrumb } from './subject'
import { type CanvasViewMode, ViewModeMenu } from './ViewModeMenu'

export function CanvasPanel({
  file,
  projectId,
  breadcrumbs,
  focused = false,
  tabs = [],
  onSelectTab,
  onOpenTab,
  onCloseTab,
  sideOpen,
  onToggleSide,
  onClose,
  onSplit,
  agentCollapsed,
  onShowAgent,
  composerSlotRef,
  onOpenSession,
  onOpenDocument,
  onOpenUrl,
  onWebNavigate,
  appSkillAction,
  onAppControl,
  onSubjectChange,
  app,
}: {
  file: Subject
  projectId: string
  breadcrumbs: string[]
  focused?: boolean
  /** Every file a code pane holds, in order. Drawn as tabs from two up; one
   *  file keeps the breadcrumbs, the way a pane always looked. */
  tabs?: string[]
  onSelectTab?: (docId: string) => void
  /** A code file chosen in the pane's Files column, opened here as a tab. */
  onOpenTab?: (docId: string) => void
  onCloseTab?: (docId: string) => void
  sideOpen: boolean
  onToggleSide: () => void
  onClose: () => void
  /** Put a SECOND pane on this document, beside this one. The deliberate split:
   *  opening normally reuses this pane, which is what you want almost always. */
  onSplit?: () => void
  agentCollapsed: boolean
  onShowAgent: () => void
  /** Where the one Composer portals to while the agent panel is collapsed (§3e). */
  composerSlotRef: Ref<HTMLDivElement>
  /** Host navigation for an app view's `mod.sessions.open` (RFC 0267). */
  onOpenSession?: (id: string) => void
  /** RFC 0273: a view asked the host to open a document on the canvas. */
  onOpenDocument?: (id: string, target?: LinkOpenTarget) => void
  onOpenUrl?: (url: string, target?: LinkOpenTarget) => void
  /** A web page in this pane navigated: the pane should now BE that url, so a
   *  remount, a space switch or a refresh comes back to it. */
  onWebNavigate?: (url: string) => void
  /** RFC 0334 3g: what the header tile's "Open skill" does, opening that
   *  app's skill document. Undefined for an app with no skill, so the tile
   *  stays a picture. */
  appSkillAction?: (appId: string) => (() => void) | undefined
  /** RFC 0328: publish the visible app frame's command channel to this pane. */
  onAppControl?: (controller: AppViewController | null) => void
  /** RFC 0297: the view said what it is showing, so the shell can hand THAT to
   *  the session beside it rather than the app's source. */
  onSubjectChange?: (id: string | null) => void
  /** RFC 0273: the app whose view is open, when one is, so the column can offer
   *  the sections it declares. */
  app?: AppSummary
}) {
  const headingId = useId()
  // The app's tile replaces its name crumb (RFC 0332); see withAppCrumb.
  const tileApp = useIconApp({ appId: app?.id ?? file.appId, mime: file.mime })
  const { crumbs, appIndex: appCrumb } = withAppCrumb(
    breadcrumbs,
    tileApp?.name,
  )

  // RFC 0273: what the canvas is SHOWING, which is not always what it opened.
  // Opening an app opens its view document (app source), while the thing on
  // screen is an instance the app chose, so an app declares its subject over the
  // bridge. Reset on file change: the previous view's subject must never leak
  // into the next one, and a plain document simply never declares, leaving the
  // column scoped to the file itself.
  // Stamped with the file it belongs to and DERIVED rather than reset in an
  // effect: an effect would leave one render showing the previous view's
  // subject before it fired, which is the frame where a comment column shows
  // the wrong document's comments.
  const [declared, setDeclared] = useState<{
    file: string
    id: string | null
    include: string[]
  }>({ file: file.id, id: null, include: [] })
  const forThisFile = declared.file === file.id
  const subject = forThisFile ? declared.id : null
  // Documents the view says belong to this surface (a review's plan), whose
  // comments the column aggregates with the subject's.
  const include = forThisFile ? declared.include : []
  /**
   * The document's view commands (RFC 0328), kept here too so the side column
   * can move the document to a thread's passage (RFC 0324 3e). Passed on to the
   * shell unchanged, which is what control_app reads.
   */
  const paneControlRef = useRef<AppViewController | null>(null)
  const onAppControlRef = useRef(onAppControl)
  onAppControlRef.current = onAppControl
  const handleAppControl = useCallback(
    (controller: AppViewController | null) => {
      paneControlRef.current = controller
      onAppControlRef.current?.(controller)
      // Opened from the holdings count (RFC 0324 3f): go to the passage it
      // named, now that there are commands to do it with.
      const anchor = controller
        ? takePendingPassage(fileIdRef.current)
        : undefined
      if (anchor) void scrollWhenReady(controller as AppViewController, anchor)
    },
    [],
  )
  const fileIdRef = useRef(file.id)
  fileIdRef.current = file.id
  // The count also asks for the column. Opened here rather than by the count,
  // because only this pane knows whether its column is already showing.
  const sideOpenRef = useRef(sideOpen)
  sideOpenRef.current = sideOpen
  const onToggleSideRef = useRef(onToggleSide)
  onToggleSideRef.current = onToggleSide
  useEffect(() => {
    const serve = () => {
      if (!hasPendingPassage(file.id)) return
      if (!sideOpenRef.current) onToggleSideRef.current()
      // Already mounted with commands: go now. Otherwise the commands
      // arriving (handleAppControl) takes it.
      const control = paneControlRef.current
      if (!control) return
      const anchor = takePendingPassage(file.id)
      if (anchor) void scrollWhenReady(control, anchor)
    }
    serve()
    return onPendingPassage((id) => {
      if (id === file.id) serve()
    })
  }, [file.id])
  const goToPassage = useCallback((anchorText: string) => {
    // A document with no view commands (an app that declares none) has
    // nowhere to scroll; the click does nothing rather than failing.
    void paneControlRef
      .current?.({ action: 'scrollTo', input: { anchorText } })
      .catch(() => {})
  }, [])
  const onSubjectChangeRef = useRef(onSubjectChange)
  onSubjectChangeRef.current = onSubjectChange
  const onSubject = useCallback(
    (id: string | null, inc?: string[]) => {
      setDeclared({ file: file.id, id, include: inc ?? [] })
      onSubjectChangeRef.current?.(id)
    },
    [file.id],
  )

  // Which side section is showing. Held here rather than inside the column
  // because the tabs ARE the panel header, so the header and the body both need
  // it. Resolved against the sections that currently exist, so a section that
  // disappears falls back rather than leaving the column blank.
  const sections = useMemo(
    () =>
      sideSections(app, {
        // A project file has a History too: its git log (RFC 0313 3g, 3n).
        history:
          !isWebId(file.id) && !isHistoryId(file.id) && !isBranchId(file.id),
      }),
    [app, file.id],
  )
  const [selected, setSelected] = useState<string>(() => sections[0].id)

  // Reset to the rendered app when the canvas changes: source mode is a thing
  // you go and look at, not a preference that should follow you to the next
  // document.
  const [viewMode, setViewMode] = useState<CanvasViewMode>('app')
  const [modeFor, setModeFor] = useState(file.id)
  const activeMode = modeFor === file.id ? viewMode : 'app'
  const activeSection = resolveSection(selected, sections)

  // RFC 0313 3c: a saved version shown in this pane from History's View.
  // Keyed to the document it was taken from, the same way as the view mode,
  // so opening another document is never shown an old version of the last.
  const [preview, setPreview] = useState<{
    fileId: string
    version: VersionPreview
  } | null>(null)
  const version = preview?.fileId === file.id ? preview.version : null
  const viewVersion = (v: VersionPreview | null) =>
    setPreview(v ? { fileId: file.id, version: v } : null)

  /**
   * Reading mode takes the side column away for as long as it lasts.
   *
   * Not by turning the toggle off, which would make leaving preview lose a
   * setting the person made in a different mode; the column is simply not
   * mounted while reading, and comes back exactly as it was. Preview is a way of
   * LOOKING at the document, and looking at something should not quietly rewrite
   * how the pane is configured.
   */
  const reading = activeMode === 'preview'

  // A web page is an open item like any other, but it is not a document: it has
  // no Automerge doc to hydrate and therefore nothing for the side column to be
  // about. So the column (and its control) are simply not offered — a Comments
  // tab that can never hold a comment is worse than no tab.
  const webUrl = webUrlOf(file.id)
  // A project file (RFC 0277 S6) is not a document either: no comments, no
  // data, no source mode, because the canvas is already showing its source.
  const isCode = isCodeId(file.id)
  // RFC 0313 3i: a project's History is a pane about the project, not a
  // document: no comments, no tasks, no view modes.
  const historyOf = historyProjectOf(file.id)
  const branchPane = branchPaneOf(file.id)
  const notADocument =
    isWebId(file.id) || isCode || historyOf !== null || branchPane !== null
  // A code file takes comments like a document (RFC 0277 S13); a web page does
  // not. Tasks and view modes stay a document's.
  const commentable =
    !isWebId(file.id) && historyOf === null && branchPane === null
  const showSide = sideOpen && commentable && !reading
  const side = useSideWidth()
  // RFC 0354: a code pane's own Files column, on its left. Per device.
  const [filesPrefs, setFilesPrefs] = useFilesPrefs()
  const showFiles = isCode && filesPrefs.open && onOpenTab !== undefined
  const [projectDoc] = useDocument(projectId as AnyDocumentId)
  const panelRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const panel = panelRef.current
    if (!panel || !onOpenDocument) return
    const openSource = (event: Event) => {
      const id = (event as CustomEvent<{ id?: string }>).detail?.id
      if (!id) return
      const source = parseCodeId(id)
      if (
        source &&
        projectDoc &&
        projectSources(projectDoc).sources.some(
          (entry) => entry.id === source.sourceId,
        )
      )
        onOpenDocument(id)
    }
    panel.addEventListener('mod-comment-source-open', openSource)
    return () =>
      panel.removeEventListener('mod-comment-source-open', openSource)
  }, [onOpenDocument, projectDoc])

  return (
    <div ref={panelRef} className="flex h-full min-w-0 flex-1">
      <div className="flex min-w-0 flex-1 flex-col">
        <Panel
          focused={focused}
          // The side panel draws its own frame, so the canvas does not double it.
          // A web page never shows one, so it keeps its frame regardless of the
          // toggle's remembered state.
          framed={!showSide}
          header={
            <>
              {/* Surfaced only while the agent panel is hidden, as legacy did. */}
              {agentCollapsed && (
                <PanelAction title="Agent panel" onClick={onShowAgent}>
                  <MessageCircle size={14} />
                </PanelAction>
              )}
              {isCode && onOpenTab && (
                <PanelAction
                  title="Files: what this branch changed, or every file"
                  onClick={() => setFilesPrefs({ open: !filesPrefs.open })}
                  active={filesPrefs.open}
                >
                  <PanelLeft size={14} />
                </PanelAction>
              )}
              {tabs.length > 1 ? (
                <CodeTabs
                  tabs={tabs}
                  active={file.id}
                  onSelect={(id) => onSelectTab?.(id)}
                  onClose={(id) => onCloseTab?.(id)}
                />
              ) : (
                <nav
                  aria-labelledby={headingId}
                  className="flex min-w-0 flex-1 items-center gap-1"
                >
                  <span id={headingId} className="sr-only">
                    File path
                  </span>
                  {crumbs.map((crumb, i) => {
                    const isLast = i === crumbs.length - 1
                    // The app's own crumb is its tile (RFC 0332): the app name
                    // is replaced, not repeated beside it. Named for a screen
                    // reader and on hover.
                    const isApp = tileApp !== undefined && i === appCrumb
                    return (
                      <span
                        key={`${crumb}-${i === 0 ? 'first' : isLast ? 'last' : i}`}
                        className="flex min-w-0 items-center gap-1"
                      >
                        {isApp ? (
                          <AppTile
                            appId={tileApp.id}
                            name={crumb}
                            onOpenSkill={appSkillAction?.(tileApp.id)}
                          />
                        ) : (
                          <span
                            title={crumb}
                            className={`truncate text-xs ${
                              isLast
                                ? 'font-medium text-foreground'
                                : 'text-muted-foreground'
                            }`}
                          >
                            {crumb}
                          </span>
                        )}
                        {!isLast && (
                          <span className="shrink-0 text-muted-foreground/40">
                            /
                          </span>
                        )}
                      </span>
                    )
                  })}
                </nav>
              )}
              {!notADocument && (
                <ShareTarget
                  documentId={file.id}
                  kind="document"
                  projectId={projectId}
                />
              )}
              {/* Who else is in this document (RFC 0311 3d). */}
              {!notADocument && import.meta.env.VITE_MOD_KEYHIVE !== '1' && (
                <DocumentHolders
                  projectId={projectId}
                  documentId={file.id}
                  onOpenSession={onOpenSession}
                />
              )}
              {commentable && !reading && (
                <DocumentCommentsButton
                  projectId={projectId}
                  documentId={file.id}
                  active={sideOpen}
                  onToggle={onToggleSide}
                />
              )}
              {!notADocument && (
                <DocumentTasks projectId={projectId} documentId={file.id} />
              )}
              {!notADocument && (
                <ViewModeMenu
                  mode={activeMode}
                  onSelect={(m) => {
                    setViewMode(m)
                    setModeFor(file.id)
                  }}
                />
              )}
              {/* Hidden rather than disabled while reading: a control whose
                  only effect is to leave the mode you chose is a trap, and the
                  mode menu beside it is the way out. */}
              {commentable && !reading && (
                <PanelAction
                  title="Toggle side panel (⌘\)"
                  onClick={onToggleSide}
                  active={sideOpen}
                >
                  <PanelRight size={14} />
                </PanelAction>
              )}
              {onSplit && !reading && (
                <PanelAction
                  title="Open in split — a second pane on this document"
                  onClick={onSplit}
                >
                  <Columns2 size={14} />
                </PanelAction>
              )}
              <PanelAction title="Close file (esc)" onClick={onClose}>
                <X size={14} />
              </PanelAction>
            </>
          }
        >
          {/* rounded-[inherit] carries the panel body's radius down to the
              iframe. Panel decides that radius conditionally (`framed`), so
              hardcoding it here would round the frame even when the panel is
              deliberately unframed — which shows as stray corners. */}
          <div className="relative flex h-full flex-col rounded-[inherit]">
            {webUrl ? (
              <WebView url={webUrl} onNavigate={onWebNavigate} />
            ) : branchPane ? (
              <BranchView
                projectId={branchPane.projectId}
                name={branchPane.name}
                {...(onOpenDocument ? { onOpenDocument } : {})}
              />
            ) : historyOf ? (
              <ProjectHistoryView
                projectId={historyOf}
                {...(onOpenDocument ? { onOpenDocument } : {})}
                {...(onOpenSession ? { onOpenSession } : {})}
              />
            ) : file.missingApp ? (
              <MissingAppView
                file={file}
                projectId={projectId}
                dataType={file.missingApp}
                onOpenApp={(appId) => onOpenDocument?.(appId)}
              />
            ) : isCode ? (
              <div className="flex min-h-0 flex-1">
                {showFiles && (
                  <CodeFilesColumn
                    projectId={projectId}
                    activeId={file.id}
                    mode={filesPrefs.mode}
                    onMode={(mode) => setFilesPrefs({ mode })}
                    width={filesPrefs.width}
                    onWidth={(width) => setFilesPrefs({ width })}
                    onOpen={(id) => onOpenTab?.(id)}
                  />
                )}
                <div className="flex min-w-0 flex-1 flex-col">
                  <CodeFileView
                    file={file}
                    projectId={projectId}
                    Comments={DocumentComments}
                    diffMode={filesPrefs.mode === 'changed'}
                    onOpenCodeFile={(id) => onOpenTab?.(id)}
                  />
                </div>
              </div>
            ) : version ? (
              <VersionPreviewPane
                preview={version}
                mime={file.mime}
                projectId={projectId}
                documentId={file.id}
                onClose={() => viewVersion(null)}
              />
            ) : (
              <DocumentView
                file={file}
                projectId={projectId}
                onOpenSession={onOpenSession}
                onOpenDocument={onOpenDocument}
                onOpenUrl={onOpenUrl}
                onAppControl={handleAppControl}
                onSubject={onSubject}
                viewMode={activeMode}
              />
            )}
            {/* The compact composer's destination. Empty until the agent panel
                collapses, at which point the one Composer portals in here. */}
            <div ref={composerSlotRef} className="shrink-0 empty:hidden" />
          </div>
        </Panel>
      </div>

      {showSide && (
        <div className="relative shrink-0" style={{ width: side.width }}>
          {/* Drag to resize; double-click for the default width. */}
          <button
            type="button"
            aria-label="Resize the side column"
            title="Drag to resize, double-click to reset"
            onPointerDown={side.onHandleDown}
            onDoubleClick={side.reset}
            className="absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize bg-transparent hover:bg-blue-500/30"
          />
          <Panel
            framed={false}
            header={
              <SidePanelTabs
                sections={sections}
                active={activeSection}
                onSelect={setSelected}
              />
            }
          >
            <SidePanelBody
              active={activeSection}
              file={file}
              projectId={projectId}
              subject={subject}
              include={include}
              onOpenSession={onOpenSession}
              onGoToPassage={goToPassage}
              {...(onOpenDocument ? { onOpenDocument } : {})}
              onViewVersion={viewVersion}
              viewingVersionId={version?.id ?? null}
            />
          </Panel>
        </div>
      )}
    </div>
  )
}

/**
 * A code pane's files, as tabs in its header. The label is the file name; the
 * full title, with its branch, is on hover, so two copies of one file on two
 * branches can still be told apart.
 */
function CodeTabs({
  tabs,
  active,
  onSelect,
  onClose,
}: {
  tabs: string[]
  active: string
  onSelect: (id: string) => void
  onClose: (id: string) => void
}) {
  return (
    <div
      role="tablist"
      aria-label="Open files"
      className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto"
    >
      {tabs.map((id) => {
        const subject = codeFileFromId(id)
        const name = subject?.path?.split('/').pop() ?? id
        const selected = id === active
        return (
          <div
            key={id}
            className={`group flex min-w-0 max-w-48 shrink-0 items-center rounded ${
              selected
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
            }`}
          >
            <button
              type="button"
              role="tab"
              aria-selected={selected}
              title={subject?.title ?? id}
              onClick={() => onSelect(id)}
              className={`min-w-0 truncate py-0.5 pl-1.5 text-xs ${
                selected ? 'font-medium' : ''
              }`}
            >
              {name}
            </button>
            <button
              type="button"
              aria-label={`Close ${name}`}
              title="Close tab"
              onClick={() => onClose(id)}
              className={`mx-0.5 shrink-0 rounded p-0.5 hover:bg-muted ${
                selected ? '' : 'opacity-0 group-hover:opacity-100'
              }`}
            >
              <X size={11} />
            </button>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Scroll to a passage once the document can find it. The commands register
 * when the editor mounts, which can be before its text has loaded, and a
 * passage that is not there yet is an error from `scrollTo`. So try for a few
 * seconds, then give up quietly: the document is open, which is most of it.
 */
async function scrollWhenReady(
  control: AppViewController,
  anchorText: string,
): Promise<void> {
  for (let i = 0; i < 12; i++) {
    try {
      await control({ action: 'scrollTo', input: { anchorText } })
      return
    } catch {
      await new Promise((r) => setTimeout(r, 250))
    }
  }
}
